import React, { useState, useEffect } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { DEFAULT_SIDEBAR } from '@/components/v2/defaults';
import SidebarProfileV2 from '@/components/v2/SidebarProfileV2';
import type { SidebarItem } from '@/components/v2/types';
import { useAuth } from '../../contexts/AuthContext';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const SidebarV2: React.FC<{
  items?: SidebarItem[];
  activePath?: string;
  onNavigate?: (p: string) => void;
  isAdmin?: boolean;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}> = ({
  items = DEFAULT_SIDEBAR,
  activePath = '/dashboard',
  onNavigate,
  isAdmin,
  isCollapsed: externalCollapsed,
  onCollapsedChange,
}) => {
  // Get actual user from AuthContext
  const { user } = useAuth();
  const userName = user?.name || 'User';
  const userRole = user?.role || 'Member';
  const userEmail = user?.email || '';
  const isUserAdmin = isAdmin || user?.role === 'admin' || user?.role === 'super_admin';

  // Get tenant name - you can enhance this by fetching from API if needed
  // For now, use a placeholder or extract from user metadata
  const tenantName = (user as any)?.tenantName || (user as any)?.firmName || undefined;

  // Collapse state - use external if provided, otherwise internal with localStorage persistence
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });

  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  // Wrap setIsCollapsed to persist to localStorage
  const setIsCollapsed = (collapsed: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(collapsed);
    } else {
      setInternalCollapsed(collapsed);
    }
    // Always persist to localStorage
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  const [isHovered, setIsHovered] = useState(false);

  // Determine if sidebar should show full content
  const showFullContent = !isCollapsed || isHovered;

  return (
    <aside
      data-sidebar
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`sidebar-legacy fixed left-0 top-0 hidden h-screen border-r border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 md:block transition-all duration-300 ease-in-out ${
        showFullContent ? 'w-[280px] shadow-sm' : 'w-[72px]'
      } ${isCollapsed && isHovered ? 'shadow-2xl z-50' : 'z-40'}`}
      aria-label="Sidebar"
    >
      <div className="flex h-full flex-col p-3 relative">
        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`absolute -right-3 top-8 z-50 h-6 w-6 rounded-full border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-500 hover:border-amber-300 dark:hover:border-amber-600 ${
            showFullContent ? '' : 'opacity-0 hover:opacity-100'
          }`}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </button>

        <SidebarProfileV2
          name={userName}
          role={userRole}
          email={userEmail}
          tenantName={tenantName}
          isAdmin={isUserAdmin}
          isCollapsed={!showFullContent}
          onSettings={() => onNavigate?.('/settings')}
        />

        <div className="mt-3 space-y-4 overflow-y-auto">
          <div>
            {showFullContent && (
              <div className="px-2 pb-1 text-[11px] font-normal uppercase tracking-normal text-slate-500 dark:text-neutral-400 subheading transition-opacity duration-300">
                Main
              </div>
            )}
            <nav className="space-y-1">
              {items
                .filter(i =>
                  [
                    'Dashboard',
                    'Notifications',
                    'Email',
                    'Email V2 (Live)',
                    'Omniscient Agent',
                    'AI Assistant',
                    'AI Assistant v2 (Preview)',
                    'Whiteboard',
                    'Whiteboard V2 (Preview)',
                    'GameTheoryV2 (Preview)',
                    'MindMapping Smart',
                    'FastLearn',
                    'FastLearn v2 (Preview)',
                    'Presentations Hub',
                    'NotebookLM',
                    'Smart Kitchen',
                    'Ideas Kitchen',
                    'Smart Notebook',
                    'Intelligence Studio',
                  ].includes(i.label)
                )
                .map(it => {
                  const active = activePath === it.path;
                  return (
                    <button
                      key={it.path}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onNavigate?.(it.path)}
                      className={`btn relative flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-normal transition-all duration-200 ${
                        active
                          ? 'border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                          : 'border border-transparent hover:bg-slate-50 dark:hover:bg-neutral-800'
                      } ${!showFullContent ? 'justify-center px-2' : ''}`}
                      title={!showFullContent ? it.label : undefined}
                    >
                      {active && showFullContent && (
                        <span className="absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-amber-500" />
                      )}
                      <span
                        className={`${active ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-neutral-200'} ${!showFullContent ? 'text-lg' : ''}`}
                      >
                        {it.icon}
                      </span>
                      {showFullContent && <span className="truncate subheading">{it.label}</span>}
                    </button>
                  );
                })}
            </nav>
          </div>

          <div>
            {showFullContent && (
              <div className="px-2 pb-1 text-[11px] font-normal uppercase tracking-normal text-slate-500 dark:text-neutral-400 subheading transition-opacity duration-300">
                Work
              </div>
            )}
            <nav className="space-y-1">
              {items
                .filter(i =>
                  [
                    'Clients',
                    'Cases',
                    'Contracts',
                    'Contracts Analysis V1',
                    'Contracts Analysis V2',
                    'Documents',
                    'Excel Intelligence',
                    'Excel Intelligence V2',
                    'Excel Intelligence V3',
                    'Tasks',
                    'Tasks V2 (Preview)',
                    'Tasks V3 Enhanced',
                    'Tasks V4 (Preview)',
                    'Tasks V5',
                    'Calendar',
                  ].includes(i.label)
                )
                .sort((a, b) => {
                  const order = [
                    'Clients',
                    'Cases',
                    'Contracts',
                    'Contracts Analysis V1',
                    'Contracts Analysis V2',
                    'Documents',
                    'Excel Intelligence',
                    'Excel Intelligence V2',
                    'Excel Intelligence V3',
                    'Tasks',
                    'Tasks V2 (Preview)',
                    'Tasks V3 Enhanced',
                    'Tasks V4 (Preview)',
                    'Tasks V5',
                    'Calendar',
                  ];
                  return order.indexOf(a.label) - order.indexOf(b.label);
                })
                .map(it => {
                  const active = activePath === it.path;
                  return (
                    <button
                      key={it.path}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onNavigate?.(it.path)}
                      className={`btn relative flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-normal transition-all duration-200 ${
                        active
                          ? 'border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                          : 'border border-transparent hover:bg-slate-50 dark:hover:bg-neutral-800'
                      } ${!showFullContent ? 'justify-center px-2' : ''}`}
                      title={!showFullContent ? it.label : undefined}
                    >
                      {active && showFullContent && (
                        <span className="absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-amber-500" />
                      )}
                      <span
                        className={`${active ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-neutral-200'} ${!showFullContent ? 'text-lg' : ''}`}
                      >
                        {it.icon}
                      </span>
                      {showFullContent && <span className="truncate subheading">{it.label}</span>}
                    </button>
                  );
                })}
            </nav>
          </div>

          <div>
            {showFullContent && (
              <div className="px-2 pb-1 text-[11px] font-normal uppercase tracking-normal text-slate-500 dark:text-neutral-400 subheading transition-opacity duration-300">
                Resources
              </div>
            )}
            <nav className="space-y-1">
              {items
                .filter(i => ['Legal Research', 'Legal Research V2', 'Library', 'Forms'].includes(i.label))
                .sort((a, b) => {
                  const order = ['Legal Research', 'Legal Research V2', 'Library', 'Forms'];
                  return order.indexOf(a.label) - order.indexOf(b.label);
                })
                .map(it => {
                  const active = activePath === it.path;
                  return (
                    <button
                      key={it.path}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onNavigate?.(it.path)}
                      className={`btn relative flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-normal transition-all duration-200 ${
                        active
                          ? 'border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                          : 'border border-transparent hover:bg-slate-50 dark:hover:bg-neutral-800'
                      } ${!showFullContent ? 'justify-center px-2' : ''}`}
                      title={!showFullContent ? it.label : undefined}
                    >
                      {active && showFullContent && (
                        <span className="absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-amber-500" />
                      )}
                      <span
                        className={`${active ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-neutral-200'} ${!showFullContent ? 'text-lg' : ''}`}
                      >
                        {it.icon}
                      </span>
                      {showFullContent && <span className="truncate subheading">{it.label}</span>}
                    </button>
                  );
                })}
            </nav>
          </div>

          <div>
            {showFullContent && (
              <div className="px-2 pb-1 text-[11px] font-normal uppercase tracking-normal text-slate-500 dark:text-neutral-400 subheading transition-opacity duration-300">
                Admin
              </div>
            )}
            <nav className="space-y-1">
              {items
                .filter(i => ['Settings'].includes(i.label))
                .map(it => {
                  const active = activePath === it.path;
                  return (
                    <button
                      key={it.path}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onNavigate?.(it.path)}
                      className={`btn relative flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-normal transition-all duration-200 ${
                        active
                          ? 'border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                          : 'border border-transparent hover:bg-slate-50 dark:hover:bg-neutral-800'
                      } ${!showFullContent ? 'justify-center px-2' : ''}`}
                      title={!showFullContent ? it.label : undefined}
                    >
                      {active && showFullContent && (
                        <span className="absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-amber-500" />
                      )}
                      <span
                        className={`${active ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-neutral-200'} ${!showFullContent ? 'text-lg' : ''}`}
                      >
                        {it.icon}
                      </span>
                      {showFullContent && <span className="truncate subheading">{it.label}</span>}
                    </button>
                  );
                })}
            </nav>
          </div>
        </div>

        {showFullContent ? (
          <div className="mt-4 rounded-xl border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800 p-3 text-xs text-slate-600 dark:text-neutral-400 transition-all duration-300">
            <div className="font-medium text-slate-700 dark:text-neutral-300">Workspace</div>
            Tenant‑based subscription. Seats & duration are managed by admins.
          </div>
        ) : (
          <div
            className="mt-4 rounded-xl border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800 p-2 flex items-center justify-center transition-all duration-300"
            title="Workspace"
          >
            <span className="text-lg">🏢</span>
          </div>
        )}
      </div>
    </aside>
  );
};

export default SidebarV2;
